import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useView } from '../contexts/ViewContext';
import { getBackendUrl } from '../config/api';
import { trackEvent } from '../utils/analytics';

const STORAGE_KEY = 'activation_nudge_shown';
const NEW_REG_KEY = 'is_new_registration';

const INTENTS = [
  {
    id: 'career',
    emoji: '💼',
    title: 'Career ke baare mein poochho',
    sub: 'Naukri, promotion, business — kya kehti hai kundali?',
  },
  {
    id: 'marriage',
    emoji: '💍',
    title: 'Shaadi ka timing jaanna hai',
    sub: 'Rishta, compatibility, sahi waqt kab?',
  },
  {
    id: 'health',
    emoji: '🧘',
    title: 'Health aur energy ke baare mein',
    sub: 'Planetary effects on your wellbeing',
  },
];

const within14Days = (createdAt) => {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (isNaN(created)) return false;
  const fourteenDays = 14 * 24 * 60 * 60 * 1000;
  return Date.now() - created <= fourteenDays;
};

const ActivationNudge = ({ user }) => {
  const view = useView();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem(STORAGE_KEY) === 'true') return;
    if (!within14Days(user.created_at)) return;

    let cancelled = false;
    const isNewReg = localStorage.getItem(NEW_REG_KEY) === 'true';

    (async () => {
      try {
        const token = localStorage.getItem('jyotish_session_token');
        if (!token) return;
        const res = await fetch(`${getBackendUrl()}/api/user/wallet`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const balance = data.total_balance ?? 0;
        if (balance < 5000) return;

        const delay = isNewReg ? 500 : 2500;
        const t = setTimeout(() => {
          if (cancelled) return;
          setVisible(true);
          if (isNewReg) localStorage.removeItem(NEW_REG_KEY);
        }, delay);
        return () => clearTimeout(t);
      } catch {}
    })();

    return () => { cancelled = true; };
  }, [user]);

  const markShown = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
  };

  const handleIntentSelect = (intent) => {
    markShown();
    trackEvent('activation_nudge_intent_selected', { intent });
    // Pre-select intent for ChitraGupt session
    sessionStorage.setItem('chitragupt_preselected_intent', intent);
    if (view?.navigateTo) {
      view.navigateTo('chat');
    } else {
      // TODO: fall back to direct path if ViewContext is unavailable
      window.location.href = '/chitragupt';
    }
  };

  const handleDismiss = () => {
    markShown();
    trackEvent('activation_nudge_dismissed', {});
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          onClick={handleDismiss}
          style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-base)',
              borderTop: '2px solid rgba(212,175,55,0.5)',
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              padding: '28px 20px 40px',
              width: '100%',
              maxWidth: 480,
              boxShadow: '0 -16px 48px rgba(0,0,0,0.6)',
              fontFamily: 'Montserrat, sans-serif',
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: 18 }}>
              <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 10 }}>🔮</div>
              <h2 style={{
                fontFamily: 'Cinzel, serif', fontSize: 20, fontWeight: 700,
                color: '#e2d9f3', margin: 0,
              }}>
                Aapka 599 Karma tayaar hai!
              </h2>
              <p style={{
                fontSize: 13, color: 'var(--gold-primary)', margin: '6px 0 0', fontWeight: 600,
              }}>
                Bilkul free — aaj use karo
              </p>
            </div>

            <p style={{
              fontSize: 12.5, color: '#cbd5e1', textAlign: 'center',
              lineHeight: 1.6, margin: '0 0 22px',
            }}>
              ChitraGupt AI se apna pehla sawaal poochho.<br/>
              Career, shaadi, ya health — koi bhi cheez.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {INTENTS.map((it) => (
                <button
                  key={it.id}
                  onClick={() => handleIntentSelect(it.id)}
                  data-testid={`activation-intent-${it.id}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    textAlign: 'left', paddingLeft: 16, paddingRight: 16,
                    paddingTop: 14, paddingBottom: 14,
                    borderRadius: 12,
                    border: '1px solid rgba(212,175,55,0.35)',
                    background: 'rgba(212,175,55,0.06)',
                    color: '#fff', cursor: 'pointer',
                    transition: 'background 0.15s, border-color 0.15s',
                    width: '100%',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(212,175,55,0.14)';
                    e.currentTarget.style.borderColor = 'rgba(212,175,55,0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(212,175,55,0.06)';
                    e.currentTarget.style.borderColor = 'rgba(212,175,55,0.35)';
                  }}
                >
                  <span style={{ fontSize: 24, flexShrink: 0 }}>{it.emoji}</span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: '#f5e6c8' }}>
                      {it.title}
                    </span>
                    <span style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.4 }}>
                      {it.sub}
                    </span>
                  </span>
                </button>
              ))}
            </div>

            <button
              onClick={handleDismiss}
              data-testid="activation-nudge-dismiss"
              style={{
                display: 'block', margin: '14px auto 0',
                background: 'none', border: 'none',
                color: 'rgba(255,255,255,0.4)', fontSize: 12,
                padding: '8px 12px', cursor: 'pointer',
                fontFamily: 'Montserrat, sans-serif',
              }}
            >
              Baad mein karunga / karungi →
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ActivationNudge;
